import React, { useCallback, useState, useEffect } from 'react'
import CsvDownloader from 'react-csv-downloader'
import { observer } from 'mobx-react'
import moment from 'moment'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import DateRangeIcon from '@mui/icons-material/DateRange'
import makeStyles from '@mui/styles/makeStyles'

import * as srv from 'admin/services'
import { formatDate, formatTimeOnly } from 'admin/utils/helper'
import { useStores, useApiRequest } from 'admin/hooks'
import Page from 'admin/components/page'
import DateRangePicker from 'admin/components/date-range-picker'

import TransferRequestInfo from './info'
import TransferRequestDataGrid from './datagrid'
import styles from './styles'

const useStyles = makeStyles(styles)

const FILENAME = `DMS_TransferRequest_${moment().format('DDMMYY')}`

const columns = [
  {
    id: 'referenceId',
    displayName: 'Reference ID',
  },
  {
    id: 'createdAt',
    displayName: 'Date & Time',
  },
  {
    id: 'description',
    displayName: 'Description',
  },
  {
    id: 'paymentMethodType',
    displayName: 'Method',
  },
  {
    id: 'amount',
    displayName: 'Amount',
  },
  {
    id: 'currency',
    displayName: 'Currency',
  },
]

const Filters = observer(() => {
  const classes = useStyles()
  const { transferRequestStore } = useStores()

  const [anchorEl, setAnchorEl] = useState(null)
  const [date, setDate] = useState({
    key: 'selection',
    startDate: transferRequestStore.startDate,
    endDate: transferRequestStore.endDate,
  })

  const { request: exportTransferRequest, isLoading: isExporting } = useApiRequest(
    srv.fetchTransferRequest,
    {
      blocking: true,
    },
  )

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = useCallback((value) => {
    const { startDate, endDate } = value.selection
    setDate((prev) => ({
      ...prev,
      startDate,
      endDate,
    }))
    transferRequestStore.updateStartDate(startDate)
    transferRequestStore.updateEndDate(endDate)
  }, [])

  const getDataTransferRequest = async () => {
    const sDate = moment(transferRequestStore.startDate).startOf('day').toISOString()
    const eDate = moment(transferRequestStore.endDate).endOf('day').toISOString()
    const params = {
      startDate: sDate,
      endDate: eDate,
      sortBy: 'createdAt',
      sortDirection: 'asc',
    }

    const response = await exportTransferRequest(params)
    return response.data.list.map((item) => {
      const data = {
        referenceId: item.referenceId,
        createdAt: `${formatDate(item.createdAt)} ${formatTimeOnly(item.createdAt)}`,
        description: item.description,
        paymentMethodType: item.paymentMethodType,
        amount: item.amount,
        currency: item.currency,
      }
      return data
    })
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1rem',
      }}
    >
      <div className={classes.filterWrapper}>
        <div className={classes.actionExport}>
          <CsvDownloader
            filename={FILENAME}
            extension=".csv"
            separator=","
            wrapColumnChar='"'
            columns={columns}
            datas={getDataTransferRequest}
            text="EXPORT"
          >
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              className={classes.buttonExport}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting..' : 'Export'}
            </Button>
          </CsvDownloader>
        </div>
        <div className={classes.datePickerStartEnd}>
          <IconButton
            onClick={handleClick}
            size="large"
          >
            <DateRangeIcon />
          </IconButton>
          <span>
            {formatDate(date.startDate)} - {formatDate(date.endDate)}
          </span>
        </div>
        <DateRangePicker
          anchorEl={anchorEl}
          handleClose={handleClose}
          handleSelect={handleSelect}
          selectionRange={date}
        />
      </div>
    </Box>
  )
})

const TransferRequest = observer(() => {
  const { transferRequestStore } = useStores()

  useEffect(() => {
    transferRequestStore.fetch()
  }, [transferRequestStore.queryParams])

  useEffect(() => {
    return () => {
      transferRequestStore.resetQuery()
    }
  }, [])

  return (
    <Page>
      <Filters />
      <TransferRequestDataGrid />
      <TransferRequestInfo />
    </Page>
  )
})

export default TransferRequest
